import { ArrowLeft, Lock, Plus, X } from "lucide-react";
import { type PropsWithChildren, useState } from "react";
import { toast } from "sonner";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import type { AccountDetail } from "@/hooks/use-accounts";
import {
  useAccount,
  useAccountConstraints,
  useAddAccountConstraint,
  useConstraintLibrary,
  usePatchAccount,
  usePatchExpectationLevel,
  useRemoveAccountConstraint,
} from "@/hooks/use-accounts";
import { useTiersWithDefaultShape } from "@/hooks/use-pod-shapes";
import type { NymblRole } from "@/lib/auth";
import { useSession } from "@/lib/auth-client";
import { EXPECTATION_LEVELS } from "@/lib/expectation-levels";
import { PERMISSION_MATRIX } from "@/lib/permissions";
import type { ConstraintGroupName } from "@/schemas/accounts";
import { TierChangeSuggestionDialog } from "./TierChangeSuggestionDialog";

interface Props {
  accountId: string;
  onBack: () => void;
}

function hasCapability(
  role: string | null | undefined,
  capability: keyof (typeof PERMISSION_MATRIX)[NymblRole],
): boolean {
  if (!role) return false;
  const nymblRole = role as NymblRole;
  const matrix = PERMISSION_MATRIX[nymblRole];
  if (!matrix) return false;
  return matrix[capability] !== undefined;
}

/** Canonical tier progression for the segmented control (not alphabetical). */
const TIER_ORDER = ["Support", "Growth", "Standard", "Strategic"];

const GROUP_ORDER: ConstraintGroupName[] = ["Domain", "Platform", "Offering", "ICP"];

// Constraint-group dots use the --constraint-* product tokens so the group hue
// matches the density matrix everywhere (see src/lib/constraint-colors.ts).
const GROUP_DOT_CLASS: Record<ConstraintGroupName, string> = {
  Domain: "bg-[var(--constraint-domain)]",
  Platform: "bg-[var(--constraint-platform)]",
  Offering: "bg-[var(--constraint-offering)]",
  ICP: "bg-[var(--constraint-icp)]",
};

/** Draft holds only the fields the user has touched; undefined = untouched. */
interface Draft {
  name?: string;
  industry?: string;
  externalNymblAccountId?: string;
  tierId?: string;
  expectationLevel?: number;
}

function CardShell({ title, hint, children }: PropsWithChildren<{ title: string; hint?: string }>) {
  return (
    <section className="mb-[18px] overflow-hidden rounded-lg border border-border bg-card shadow-sm">
      <div className="flex items-center gap-2.5 border-b border-border px-5 py-3.5">
        <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          {title}
        </span>
        {hint && <span className="text-[11px] text-muted-foreground/70">{hint}</span>}
      </div>
      <div className="px-5 py-4">{children}</div>
    </section>
  );
}

function FieldRow({
  label,
  alignTop,
  children,
}: PropsWithChildren<{ label: string; alignTop?: boolean }>) {
  return (
    <div
      className={`grid grid-cols-[180px_1fr] gap-[18px] border-t border-border py-3 first:border-t-0 ${
        alignTop ? "items-start" : "items-center"
      }`}
    >
      <span
        className={`text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground ${
          alignTop ? "pt-2" : ""
        }`}
      >
        {label}
      </span>
      {children}
    </div>
  );
}

function ConstraintProfileCard({ account, canEdit }: { account: AccountDetail; canEdit: boolean }) {
  const constraintsQuery = useAccountConstraints(account.id);
  const libraryQuery = useConstraintLibrary();
  const addMutation = useAddAccountConstraint(account.id);
  const removeMutation = useRemoveAccountConstraint(account.id);

  async function handleAdd(constraintId: string, name: string) {
    try {
      await addMutation.mutateAsync(constraintId);
      toast.success(`Added "${name}" to the account profile.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to add constraint.");
    }
  }

  async function handleRemove(constraintId: string, name: string) {
    try {
      await removeMutation.mutateAsync(constraintId);
      toast.success(`Removed "${name}" from the account profile.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to remove constraint.");
    }
  }

  return (
    <CardShell title="Constraint Profile" hint="· inherited by new Solutions at creation time">
      {constraintsQuery.isLoading && (
        <div className="space-y-2 py-1">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </div>
      )}

      {constraintsQuery.isError && (
        <ErrorState
          message="Failed to load the constraint profile."
          onRetry={() => constraintsQuery.refetch()}
        />
      )}

      {!constraintsQuery.isLoading && !constraintsQuery.isError && constraintsQuery.data && (
        <div>
          {GROUP_ORDER.map((group) => {
            const items = constraintsQuery.data.groups[group] ?? [];
            const existingIds = new Set(items.map((c) => c.constraintId));
            const options = (libraryQuery.data ?? []).filter(
              (c) => c.active && c.groupName === group && !existingIds.has(c.id),
            );
            return (
              <div
                key={group}
                className="grid grid-cols-[160px_1fr] items-start gap-4 border-t border-border py-3.5 first:border-t-0"
              >
                <div className="flex items-center gap-2 pt-1.5">
                  <span
                    className={`h-2 w-2 flex-none rounded-full ${GROUP_DOT_CLASS[group]}`}
                    aria-hidden
                  />
                  <span className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                    {group}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {items.length === 0 && (
                    <span className="text-[12.5px] italic text-muted-foreground">None set</span>
                  )}
                  {items.map((c) => (
                    <span
                      key={c.constraintId}
                      className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 py-1 pl-3 pr-1 text-[12.5px] font-medium"
                    >
                      {c.constraintName}
                      {canEdit && (
                        <button
                          type="button"
                          onClick={() => handleRemove(c.constraintId, c.constraintName)}
                          disabled={removeMutation.isPending}
                          className="grid h-[18px] w-[18px] place-items-center rounded-full text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:opacity-40"
                          aria-label={`Remove ${c.constraintName}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </span>
                  ))}
                  {canEdit && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          disabled={addMutation.isPending || libraryQuery.isLoading}
                          className="inline-flex items-center gap-1 rounded-full border border-dashed border-border px-3 py-1 text-[12.5px] font-semibold text-primary transition-colors hover:border-primary hover:bg-primary/5 disabled:opacity-40"
                          aria-label={`Add ${group} constraint`}
                        >
                          <Plus className="h-3 w-3" />
                          Add
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="start"
                        className="max-h-60 min-w-[190px] overflow-y-auto"
                      >
                        {libraryQuery.isError && (
                          <DropdownMenuItem disabled>Couldn't load constraints</DropdownMenuItem>
                        )}
                        {!libraryQuery.isError && options.length === 0 && (
                          <DropdownMenuItem disabled className="italic">
                            All added
                          </DropdownMenuItem>
                        )}
                        {options.map((c) => (
                          <DropdownMenuItem key={c.id} onSelect={() => handleAdd(c.id, c.name)}>
                            {c.name}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </CardShell>
  );
}

function SkeletonProfile() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-7 w-48" />
      <Skeleton className="h-4 w-32" />
      <div className="mt-6 space-y-2">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    </div>
  );
}

export function AccountProfile({ accountId, onBack }: Props) {
  const { data: session } = useSession();
  const role = session?.user?.role;

  const canManageAccounts = hasCapability(role, "manageAccounts");
  const canSetExpectationLevel = hasCapability(role, "setAccountExpectationLevel");
  const canEditConstraints = hasCapability(role, "editAccountConstraints");
  const canEditAnything = canManageAccounts || canSetExpectationLevel;

  const { data: account, isLoading, isError, error, refetch } = useAccount(accountId);
  const tiersQuery = useTiersWithDefaultShape();

  const patchMutation = usePatchAccount(accountId);
  const expectationMutation = usePatchExpectationLevel(accountId);

  const [draft, setDraft] = useState<Draft>({});
  const [tierSuggestion, setTierSuggestion] = useState<{ tierId: string; tierName: string } | null>(
    null,
  );

  const back = (
    <Button variant="ghost" size="sm" onClick={onBack} className="mb-3.5 -ml-2">
      <ArrowLeft className="mr-1.5 h-4 w-4" />
      Accounts
    </Button>
  );

  if (isLoading) {
    return (
      <div className="mx-auto max-w-[1000px] px-6 py-6">
        {back}
        <SkeletonProfile />
      </div>
    );
  }

  if (isError || !account) {
    return (
      <div className="mx-auto max-w-[1000px] px-6 py-6">
        {back}
        <ErrorState
          message={error instanceof Error ? error.message : "Failed to load account."}
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  // Effective (draft-over-saved) values.
  const name = draft.name ?? account.name;
  const industry = draft.industry ?? account.industry ?? "";
  const externalId = draft.externalNymblAccountId ?? account.externalNymblAccountId ?? "";
  const tierId = draft.tierId ?? account.tierId;
  const expectationLevel = draft.expectationLevel ?? account.expectationLevel;

  const profileChanged =
    name !== account.name ||
    industry !== (account.industry ?? "") ||
    externalId !== (account.externalNymblAccountId ?? "");
  const tierChanged = tierId !== account.tierId;
  const expectationChanged = expectationLevel !== account.expectationLevel;
  const dirty = profileChanged || tierChanged || expectationChanged;
  const saving = patchMutation.isPending || expectationMutation.isPending;
  const nameInvalid = name.trim().length === 0;

  const tiers = [...(tiersQuery.data ?? [])].sort(
    (a, b) => TIER_ORDER.indexOf(a.name) - TIER_ORDER.indexOf(b.name),
  );

  const selectedExpectation = EXPECTATION_LEVELS.find((l) => l.level === expectationLevel);

  function setField<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSave() {
    if (!account || nameInvalid) return;
    try {
      if (profileChanged || tierChanged) {
        await patchMutation.mutateAsync({
          ...(name !== account.name ? { name: name.trim() } : {}),
          ...(industry !== (account.industry ?? "") ? { industry: industry.trim() || null } : {}),
          ...(externalId !== (account.externalNymblAccountId ?? "")
            ? { externalNymblAccountId: externalId.trim() || null }
            : {}),
          ...(tierChanged ? { tierId } : {}),
        });
      }
      if (expectationChanged) {
        const result = await expectationMutation.mutateAsync(expectationLevel);
        if (result.recalcNote) toast.info(result.recalcNote);
      }
      toast.success("Account saved.");
      setDraft({});
      if (tierChanged) {
        const newTier = tiers.find((t) => t.id === tierId);
        setTierSuggestion({ tierId, tierName: newTier?.name ?? "" });
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save account.");
    }
  }

  return (
    <div className="mx-auto max-w-[1000px] px-6 pb-20 pt-6">
      {back}

      {/* Header */}
      <div className="mb-6">
        <h1 className="font-display text-[26px] font-bold leading-tight">
          {name.trim() || "Untitled Account"}
        </h1>
        <p className="mt-1 font-mono text-[12px] text-muted-foreground">
          {account.solutionCount} Solution{account.solutionCount === 1 ? "" : "s"} ·{" "}
          {account.constraintCount} Constraint{account.constraintCount === 1 ? "" : "s"}
          {account.externalNymblAccountId
            ? ` · ${account.externalNymblAccountId.slice(0, 8)}…`
            : ""}
        </p>
      </div>

      {/* Classification — Tier + Expectation, freely assignable */}
      <CardShell title="Classification">
        <FieldRow label="Engagement Tier">
          {tiersQuery.isLoading ? (
            <Skeleton className="h-9 w-72" />
          ) : tiersQuery.isError ? (
            <ErrorState message="Failed to load tiers." onRetry={() => tiersQuery.refetch()} />
          ) : (
            <div className="inline-flex w-fit flex-wrap gap-0.5 rounded-md bg-muted p-[3px]">
              {tiers.map((tier) => {
                const on = tier.id === tierId;
                return (
                  <button
                    key={tier.id}
                    type="button"
                    disabled={!canManageAccounts}
                    onClick={() => setField("tierId", tier.id)}
                    aria-pressed={on}
                    className={`rounded-[6px] px-4 py-1.5 text-[13px] font-medium transition-colors disabled:cursor-not-allowed ${
                      on
                        ? "bg-primary text-primary-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground disabled:hover:text-muted-foreground"
                    }`}
                  >
                    {tier.name}
                  </button>
                );
              })}
            </div>
          )}
        </FieldRow>

        <FieldRow label="Expectation Level" alignTop>
          <div>
            <div className="flex flex-wrap gap-2">
              {EXPECTATION_LEVELS.map((def) => {
                const on = def.level === expectationLevel;
                return (
                  <button
                    key={def.level}
                    type="button"
                    disabled={!canSetExpectationLevel}
                    onClick={() => setField("expectationLevel", def.level)}
                    aria-pressed={on}
                    className={`min-w-[88px] flex-1 rounded-md border-[1.5px] px-2 py-3 text-center transition-all disabled:cursor-not-allowed disabled:opacity-60 ${
                      on
                        ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                        : "border-border bg-card hover:border-border hover:bg-muted/40"
                    }`}
                  >
                    <div className="font-display text-[20px] font-bold leading-none">
                      {def.level}
                    </div>
                    <div
                      className={`mt-1.5 text-[11px] font-semibold ${
                        on ? "text-primary" : "text-muted-foreground"
                      }`}
                    >
                      {def.label}
                    </div>
                  </button>
                );
              })}
            </div>
            {selectedExpectation && (
              <p className="mt-3.5 text-[13px] leading-relaxed text-muted-foreground">
                <b className="font-semibold text-foreground">
                  {selectedExpectation.level} — {selectedExpectation.label}.
                </b>{" "}
                {selectedExpectation.clientProfile}
              </p>
            )}
            {!canSetExpectationLevel && (
              <p className="mt-2 flex items-center gap-1 text-[11px] text-muted-foreground">
                <Lock className="h-3 w-3" />
                Expectation Level is set by the Resource Manager
              </p>
            )}
          </div>
        </FieldRow>
      </CardShell>

      {/* Profile — inline editable */}
      <CardShell title="Profile">
        <FieldRow label="Account Name">
          <Input
            value={name}
            disabled={!canManageAccounts}
            onChange={(e) => setField("name", e.target.value)}
            aria-invalid={nameInvalid ? "true" : undefined}
            className="max-w-[440px]"
            placeholder="Account name"
          />
        </FieldRow>
        <FieldRow label="Industry">
          <Input
            value={industry}
            disabled={!canManageAccounts}
            onChange={(e) => setField("industry", e.target.value)}
            className="max-w-[440px]"
            placeholder="e.g. Financial Services"
          />
        </FieldRow>
        <FieldRow label="External Nymbl ID">
          <Input
            value={externalId}
            disabled={!canManageAccounts}
            onChange={(e) => setField("externalNymblAccountId", e.target.value)}
            className="max-w-[440px] font-mono text-[12.5px]"
            placeholder="Optional — links to Nymbl Platform"
          />
        </FieldRow>
      </CardShell>

      {/* Constraints — inline add / remove */}
      <ConstraintProfileCard account={account} canEdit={canEditConstraints} />

      {/* Save bar */}
      {canEditAnything && (
        <div className="sticky bottom-0 mt-5 flex items-center justify-end gap-3 border-t border-border/60 bg-background/95 py-3.5 backdrop-blur">
          <span className="mr-auto text-[12px] text-muted-foreground">
            {dirty ? "Unsaved changes" : "All changes saved"}
          </span>
          <Button variant="outline" disabled={!dirty || saving} onClick={() => setDraft({})}>
            Discard
          </Button>
          <Button
            disabled={!dirty || saving || nameInvalid}
            onClick={handleSave}
            aria-busy={saving}
          >
            {saving ? "Saving…" : "Save Account"}
          </Button>
        </div>
      )}

      {tierSuggestion && (
        <TierChangeSuggestionDialog
          open={tierSuggestion !== null}
          onOpenChange={(open) => {
            if (!open) setTierSuggestion(null);
          }}
          account={{
            id: account.id,
            tierId: tierSuggestion.tierId,
            tierName: tierSuggestion.tierName,
          }}
        />
      )}
    </div>
  );
}
